import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../api/axios';
import './MeetingBookingReviews.css';

function leadIdOf(lead) { return String(lead?.leadId || lead?._id || ''); }
function leadName(lead) { return lead?.fullName || lead?.leadName || [lead?.firstName, lead?.lastName].filter(Boolean).join(' ') || lead?.email || 'Onbekende lead'; }
function payloadValue(review, ...paths) {
  for (const path of paths) {
    const value = path.split('.').reduce((current, key) => current?.[key], review?.payload);
    if (value) return value;
  }
  return '';
}

function MeetingBookingReviews() {
  const { id } = useParams();
  const [reviews, setReviews] = useState([]);
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState({});
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const [reviewResponse, campaignResponse] = await Promise.all([
        api.get(`/agent/campaigns/${id}/meeting-reviews`),
        api.get(`/campaigns/${id}`).catch(() => ({ data: {} })),
      ]);
      const campaign = campaignResponse.data?.campaign || campaignResponse.data || {};
      const campaignLeads = campaign.leads || campaign.data?.leads || [];
      setReviews(reviewResponse.data.reviews || []);
      setLeads(campaignLeads);
      setSelected((current) => {
        const next = { ...current };
        for (const review of reviewResponse.data.reviews || []) {
          if (!next[review._id]) next[review._id] = review.suggestedLeadId || review.candidateLeadIds?.[0] || '';
        }
        return next;
      });
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const leadMap = useMemo(() => new Map(leads.map((lead) => [leadIdOf(lead), lead])), [leads]);

  const resolve = async (reviewId) => {
    const leadId = selected[reviewId];
    if (!leadId) return setError('Selecteer eerst de juiste lead.');
    setBusyId(reviewId); setError(''); setNotice('');
    try {
      await api.patch(`/agent/campaigns/${id}/meeting-reviews/${reviewId}/resolve`, { leadId });
      setNotice('Afspraak is aan de lead gekoppeld en door de Agent Event Bus verwerkt.');
      await load();
    } catch (requestError) { setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message); }
    finally { setBusyId(''); }
  };

  const dismiss = async (reviewId) => {
    setBusyId(reviewId); setError(''); setNotice('');
    try {
      await api.patch(`/agent/campaigns/${id}/meeting-reviews/${reviewId}/dismiss`);
      setNotice('Bookingreview afgewezen zonder een lead of campagnedoel te wijzigen.');
      await load();
    } catch (requestError) { setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message); }
    finally { setBusyId(''); }
  };

  return <main className="meeting-reviews-page">
    <header className="meeting-reviews-header">
      <div><Link to={`/campaigns/${id}/agent`}>← Terug naar AI-campagne</Link><p>Meeting review</p><h1>Onzekere afspraken beoordelen</h1><span>Koppel een booking alleen wanneer de identiteit voldoende duidelijk is. Daarna wordt het oorspronkelijke event alsnog verwerkt.</span></div>
      <button onClick={load}>Vernieuwen</button>
    </header>
    {notice && <div className="meeting-review-notice">{notice}</div>}
    {error && <div className="meeting-review-error">{error}</div>}
    {!reviews.length ? <div className="meeting-review-empty">Er staan geen afspraken klaar voor beoordeling.</div> : <div className="meeting-review-list">
      {reviews.map((review) => {
        const suggested = review.suggestedLeadId ? leadMap.get(String(review.suggestedLeadId)) : null;
        const inviteeName = payloadValue(review, 'inviteeName', 'name', 'invitee.name');
        const inviteeEmail = payloadValue(review, 'inviteeEmail', 'email', 'invitee.email');
        const startTime = payloadValue(review, 'startTime', 'scheduled_event.start_time');
        return <article key={review._id}>
          <div className="meeting-review-title"><div><strong>{inviteeName || inviteeEmail || 'Onbekende invitee'}</strong><span>{review.provider} · {review.reason}</span></div><time>{new Date(review.createdAt).toLocaleString()}</time></div>
          <dl><div><dt>E-mail</dt><dd>{inviteeEmail || 'Niet ontvangen'}</dd></div><div><dt>Afspraak</dt><dd>{startTime ? new Date(startTime).toLocaleString() : 'Tijd onbekend'}</dd></div><div><dt>Voorgestelde lead</dt><dd>{suggested ? leadName(suggested) : 'Geen betrouwbare suggestie'}</dd></div></dl>
          <label>Juiste campagnelead
            <select value={selected[review._id] || ''} onChange={(event) => setSelected((current) => ({ ...current, [review._id]: event.target.value }))}>
              <option value="">Selecteer een lead</option>
              {leads.map((lead) => <option key={leadIdOf(lead)} value={leadIdOf(lead)}>{leadName(lead)}{lead.email ? ` — ${lead.email}` : ''}</option>)}
            </select>
          </label>
          <div className="meeting-review-actions"><button className="secondary" disabled={busyId === review._id} onClick={() => dismiss(review._id)}>Niet relevant</button><button disabled={busyId === review._id || !selected[review._id]} onClick={() => resolve(review._id)}>Aan lead koppelen</button></div>
        </article>;
      })}
    </div>}
  </main>;
}

export default MeetingBookingReviews;
